import { RefObject, useCallback, useEffect, useRef, useState } from "react";

// Anchor's viewport-relative rect. Re-snapshotted on open AND on
// every layout shift while the drop-up is open (a `requestAnimationFrame`
// loop that diffs against the previous frame, plus `window.resize`),
// so the popover follows the swatch as the central-panel resizes
// (column expand/collapse, sidebar toggle, font reflow).
//
// Why RAF over `ResizeObserver`? RO fires only on **size** changes,
// not on **position** shifts. The drop-up anchor lives inside a
// `position: absolute` editor at the bottom-right of a flex-layout
// central-panel: the swatch translates when the parent resizes
// without the swatch's own bounding rect changing size, and RO
// misses it. RAF polling is the cheap, single mechanism that catches
// both size and position shifts. The diff guard keeps React from
// re-rendering every frame when nothing moves.
export interface DropUpRect {
  left: number;
  top: number;
  width: number;
  height: number;
  bottom: number;
}

interface UseControlledDropUpArgs {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
}

interface UseControlledDropUpReturn {
  anchorRef: RefObject<HTMLButtonElement | null>;
  dropUpRef: RefObject<HTMLDivElement | null>;
  anchorRect: DropUpRect | null;
  // Helper: lets the trigger button toggle open/close in a single click
  // handler without prop-drilling `isOpen`.
  toggle: () => void;
}

// Drop-up popover that opens UPWARD from an anchor button. Closely
// parallels the tile-menu portal in `dashboard-tile-grid.tsx` — the body
// element lives in `document.body` after `createPortal`, which means CSS
// variables from the trigger's tree don't bleed in and no scrollable
// parent can clip the popover.
//
// Controlled component pattern: parent owns `isOpen` (the "at most one
// open at a time" rule is enforced by a single shared `useState` up
// there), this hook owns the imperative bits (anchor rect snapshot,
// outside-click, Escape). Inline rather than floating-ui: ~80 LOC, no
// new dep, exact fit for the picker's rectangular geometry.
export const useControlledDropUp = ({
  isOpen,
  onOpen,
  onClose,
}: UseControlledDropUpArgs): UseControlledDropUpReturn => {
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const dropUpRef = useRef<HTMLDivElement | null>(null);
  const [anchorRect, setAnchorRect] = useState<DropUpRect | null>(null);

  // Re-snapshot wrap. Called from open, `resize`, and the
  // requestAnimationFrame loop while open.
  const snapAnchorRect = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    setAnchorRect({
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
      bottom: rect.bottom,
    });
    return rect;
  }, []);

  // Capture on open; re-read on `resize` and during a RAF loop
  // for the lifetime of `isOpen === true`. The RAF loop catches
  // **position-only shifts** that ResizeObserver misses — sidebar
  // toggles, column expand/collapse, font/text reflows — anything
  // that moves the anchor without changing its size. Resize
  // listeners alone don't fire on these; RO observes *size*, not
  // *position*. RAF is the cheap, exactly-correct fix.
  useEffect(() => {
    if (!isOpen) {
      setAnchorRect(null);
      return;
    }
    snapAnchorRect();
    window.addEventListener("resize", snapAnchorRect);
    let rafId: number | null = null;
    let prevLeft = -1;
    let prevTop = -1;
    let prevWidth = -1;
    let prevHeight = -1;
    const loop = () => {
      const el = anchorRef.current;
      if (el) {
        const r = el.getBoundingClientRect();
        if (
          r.left !== prevLeft ||
          r.top !== prevTop ||
          r.width !== prevWidth ||
          r.height !== prevHeight
        ) {
          prevLeft = r.left;
          prevTop = r.top;
          prevWidth = r.width;
          prevHeight = r.height;
          setAnchorRect({
            left: r.left,
            top: r.top,
            width: r.width,
            height: r.height,
            bottom: r.bottom,
          });
        }
      }
      rafId = window.requestAnimationFrame(loop);
    };
    rafId = window.requestAnimationFrame(loop);
    return () => {
      window.removeEventListener("resize", snapAnchorRect);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [isOpen, snapAnchorRect]);

  const toggle = useCallback(() => {
    if (isOpen) onClose();
    else onOpen();
  }, [isOpen, onOpen, onClose]);

  // Outside click → close, unless the click landed on the anchor
  // (handled by the anchor's own onClick → toggle) or inside the body
  // (picker interaction).
  useEffect(() => {
    if (!isOpen) return;
    const handleDocumentMouseDown = (e: MouseEvent) => {
      const target = e.target;
      if (!(target instanceof Node)) return;
      if (anchorRef.current?.contains(target)) return;
      if (dropUpRef.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener("mousedown", handleDocumentMouseDown);
    return () =>
      document.removeEventListener("mousedown", handleDocumentMouseDown);
  }, [isOpen, onClose]);

  // Escape → close, even when the picker DOM has no focusable element
  // (the hex picker attaches tabindex on its saturation area, but if
  // a future shape doesn't, this still works).
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  return { anchorRef, dropUpRef, anchorRect, toggle };
};
